import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'tailwind.macro';
import { dayjs } from '../../libs/day';
import DatePickerCalendar, {
  DatePickerCalendarProps,
} from './DatePickerCalendar';
import DatePickerHeader from './DatePickerHeader';
import { EDatePickerModifiedDate } from './types';

const Container = styled.div`
  ${tw`w-full flex flex-col`}
  gap: var(--size-medium2);
`;

const FooterWrapper = styled.div`
  ${tw`w-full`}
`;

export interface DatePickerProps {
  startDate: dayjs.Dayjs | null;
  endDate: dayjs.Dayjs | null;

  modifiedDate: EDatePickerModifiedDate;

  isNextMonthDisabled?: (date: dayjs.Dayjs) => boolean;
  isPrevMonthDisabled?: (date: dayjs.Dayjs) => boolean;

  isOutOfRange?: (date: dayjs.Dayjs) => boolean;
  onStartDateChange: (date: dayjs.Dayjs) => void;
  onEndDateChange: (date: dayjs.Dayjs) => void;
  onUpdateModifiedDate: (type: EDatePickerModifiedDate) => void;
  footer?: React.ReactNode;
  renderDayExtraContent?: DatePickerCalendarProps['renderDayExtraContent'];
  onMonthChange?: (currentMonthDate: dayjs.Dayjs) => void;
}

const DatePicker: React.FC<DatePickerProps> = ({
  endDate,
  footer,
  isNextMonthDisabled,
  isOutOfRange,
  isPrevMonthDisabled,
  modifiedDate,
  onEndDateChange,
  onMonthChange,
  onStartDateChange,
  onUpdateModifiedDate,
  renderDayExtraContent,
  startDate,
}) => {
  const [selectedMonthOfDate, setSelectedMonthOfDate] = useState<dayjs.Dayjs>(
    startDate || dayjs(),
  );

  function onPreviousMonthClick() {
    setSelectedMonthOfDate((currentDate) => {
      const date = currentDate.add(-1, 'month');
      onMonthChange?.(date);
      return date;
    });
  }

  function onNextMonthClick() {
    setSelectedMonthOfDate((currentDate) => {
      const date = currentDate.add(1, 'month');
      onMonthChange?.(date);
      return date;
    });
  }

  return (
    <Container>
      <DatePickerHeader
        isNextMonthDisabled={isNextMonthDisabled?.(selectedMonthOfDate)}
        isPrevMonthDisabled={isPrevMonthDisabled?.(selectedMonthOfDate)}
        onNextMonthClick={onNextMonthClick}
        onPreviousMonthClick={onPreviousMonthClick}
        title={selectedMonthOfDate.format('MMMM YYYY')}
      />
      <DatePickerCalendar
        currentMonthOfDate={selectedMonthOfDate}
        endDate={endDate}
        isOutOfRange={isOutOfRange}
        modifiedDate={modifiedDate}
        onEndDateChange={onEndDateChange}
        onStartDateChange={onStartDateChange}
        onUpdateModifiedDate={onUpdateModifiedDate}
        renderDayExtraContent={renderDayExtraContent}
        startDate={startDate}
      />

      {footer && (
        <FooterWrapper data-testid="datePicker-footer">{footer}</FooterWrapper>
      )}
    </Container>
  );
};

export default DatePicker;
